import { useEffect, useState } from 'react';

import { useCentralizedProviderData, useMe } from '@crehana/auth';
import { PageLoader } from '@crehana/compass.ui/v2';
import { useRouter } from 'next/router';

/**
 * Ensures the user is always on the correct organization home URL.
 *
 * If a user logs in and accesses the Unified Home with an incorrect org slug in the URL,
 * this component automatically redirects to the authenticated user's organization home.
 * Example: /org/crehana/home/ → /org/lambda/home/
 */
export const FixUserPathBySession: React.FC<React.PropsWithChildren> = ({
  children,
}) => {
  const { me: centralizedMe, loading: isLoadingCentralizedMe } =
    useCentralizedProviderData();
  const { me: noCentralizedMe, loading: isLoadingNoCentralizedMe } = useMe();
  const { query, replace } = useRouter();
  const [isRedirecting, setIsRedirecting] = useState(false);

  const { orgSlug: pathOrganizationSlug } = query as { orgSlug: string };

  useEffect(() => {
    if (
      (centralizedMe?.user || noCentralizedMe?.organization) &&
      !isRedirecting
    ) {
      const userOrganizationSlug =
        centralizedMe?.organization.slug ?? noCentralizedMe?.organization?.slug;

      if (
        userOrganizationSlug &&
        pathOrganizationSlug !== userOrganizationSlug
      ) {
        replace(`/org/${userOrganizationSlug}/home`);
        setIsRedirecting(true);
      }
    }
  }, [
    centralizedMe?.organization.slug,
    noCentralizedMe?.organization,
    pathOrganizationSlug,
    centralizedMe?.user,
    isRedirecting,
    replace,
  ]);

  if (isLoadingCentralizedMe || isRedirecting || isLoadingNoCentralizedMe) {
    return <PageLoader />;
  }

  return <>{children}</>;
};
